import { faMagicWandSparkles } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Formik, useFormikContext, type FormikProps } from 'formik';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import {
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from 'react-router-dom';
import { toast } from 'react-toastify';
import { gql, useMutation, useQuery } from 'urql';
import * as Yup from 'yup';
import { useAppContext } from '~/App';
import { AttributeFormikField } from '~/components/AttributeForm';
import { DateTimeField } from '~/components/form/DateTimeField';
import { StatusToggle } from '~/components/form/StatusToggle';
import { SubmitButton } from '~/components/form/SubmitButton';
import { TextField } from '~/components/form/TextField';
import { TextAreaField } from '~/components/form/textarea/TextArea';
import {
  SingleUploadField,
  Upload,
  UploadResult,
} from '~/components/upload2/Upload';
import {
  AssetDocument,
  AssetInput,
  Site,
  Status,
  useAssetFiltersQuery,
  useCreateAssetMutation,
  useCreateDeploymentMutation,
  useUpdateAssetMutation,
} from '~/generated/graphql';
import { graphql } from '~/gql';
import type { Asset as AssetType } from '~/gql/graphql';
import { useDeploymentStorage } from '~/helpers/deploymentStorage';
import { convertAssetImagesToUploadResults } from '~/helpers/imageConversion';
import { SideLayout } from '~/layouts/side/SideLayout';
import { useAssetContext } from '~/routes/assets';
import {
  applyOrdering,
  extractConfiguredTypes,
  extractInUseTypes,
  getExtras,
  mergeTypes,
} from './assetFormTypes';
// import { useSite } from '~/routes/sites.$siteId';
import { PromptEffect } from '../Prompt';
import { Accordion } from '../ui/Accordion';
import { ComboboxField } from '../ui/Combobox';
import { Label } from '../ui/Label';
import { Thumb } from '../upload/Thumb';
import { CreateAssetTypeDialog } from './CreateAssetTypeDialog';
import { CreateSpaceDialog } from './CreateSpaceDialog';
import { applyAiAnalysisToForm } from './aiAnalysisApply';
import { SiteSelectField } from './DeploymentForm';

// UpdateAttributeValues mutation - same pattern as AttributeEditForm
const UpdateAttributeValues = graphql(`
  mutation UpdateAttributeValues($input: UpdateAttributeValuesInput!) {
    updateAttributeValues(input: $input) {
      ...AttributeFields
    }
  }
`);

// Query to fetch site data when we have siteId from URL context
const GetSiteForContext = gql(`
  query GetSiteForContext($id: ID!) {
    site(id: $id) {
      id
      name
      image
      status
      type
      __typename
    }
  }
`);

// Query used by ManageAssetTypes to fetch configured asset types (renamed to avoid duplicate op names)
const AssetTypeConfigForFormDocument = gql(`
  query AssetTypeConfigForForm {
    assetTypeConfig {
      types
      orderMode
      navTypes
      showAllInNav
      isCustomized
      updatedAt
    }
  }
`);

// Query to fetch attributes for AI analysis
const AssetAttributesForAI_Query = gql(`
  query AssetAttributesForAI($entityType: String!) {
    attributes(entityType: $entityType) {
      id
      name
    }
  }
`);

// Validation schema for the asset form
const validationSchema = Yup.object().shape({
  name: Yup.string()
    .trim()
    .required('Asset name is required')
    .min(1, 'Asset name cannot be empty'),
  type: Yup.string()
    .trim()
    .required('Asset type is required')
    .min(1, 'Asset type cannot be empty'),
  notes: Yup.string().trim(),
  // Deployment validation - space name required if a place is selected
  spaceName: Yup.string().when('sites', {
    is: (sites: Site[]) => sites && sites.length > 0,
    then: (schema) =>
      schema.trim().required('Space name is required when a place is selected'),
    otherwise: (schema) => schema.trim(),
  }),
});

// Define AssetFormValues type with attributes
type AssetFormValues = Omit<AssetInput, 'images'> & {
  id?: string | null;
  images: UploadResult[];
  // Keep asset_model_id for the form, it will be converted in handleSubmit
  // asset_model_id?: string[]; // Commented out - not used currently
  // Keep existing attachments structure for now if it's used differently
  attachments?: string[];
  // Add attributes section
  attributes: { id: string; value: string }[];
  // Add proper deployment fields - match DeploymentForm field names exactly
  sites?: (Site | string)[]; // Allow both Site objects and string IDs
  location?: { id: string[]; type: string }; // Renamed from deploymentLocation
  spaceName?: string; // Renamed from deploymentSpaceName
  startDate?: Date; // Renamed from deploymentStartDate
  deploymentImages?: UploadResult[]; // Keep separate from main images
};

// Move Props type definition before the component
type Props = {
  id?: string;
  isPopup?: boolean;
  onSuccess?: (assetId?: string) => void;
  asset?: AssetType | null; // Allow passing asset data directly to avoid context lookup issues
};

// Memoized component to prevent re-renders from parent
const AssetFormContent = React.memo(
  ({
    formikProps,
    assetId,
    isPopup,
    title,
    deploymentImage,
    onClose,
    isEditMode,
  }: {
    formikProps: FormikProps<AssetFormValues>;
    assetId?: string;
    isPopup?: boolean;
    title: string;
    deploymentImage?: string;
    onClose: () => void;
    isEditMode?: boolean;
  }) => {
    const { t } = useTranslation();

    // Memoize validation checks to prevent recalculation on every render
    const validationChecks = useMemo(
      () => ({
        hasAssetImage: formikProps.values.images.length > 0,
        hasWhatSectionErrors:
          !formikProps.values.name?.trim() || !formikProps.values.type?.trim(),
        hasDeploySectionErrors:
          formikProps.values.sites &&
          formikProps.values.sites.length > 0 &&
          (!formikProps.values.spaceName?.trim() ||
            !formikProps.values.startDate),
      }),
      [
        formikProps.values.images.length,
        formikProps.values.name,
        formikProps.values.type,
        formikProps.values.sites,
        formikProps.values.spaceName,
        formikProps.values.startDate,
      ]
    );

    // Memoize accordion default open state
    const accordionDefaultOpen = useMemo(() => [0, 2], []);

    // State for controlling accordion panels and highlighting
    const [openPanels, setOpenPanels] =
      useState<number[]>(accordionDefaultOpen);
    const [highlightedPanels, setHighlightedPanels] = useState<Set<number>>(
      new Set()
    );

    // State to store asset types for AI analysis
    const [assetTypes, setAssetTypes] = useState<string[]>([]);

    // Ref to store timeout IDs for cleanup
    const highlightTimeoutsRef = useRef<Map<number, NodeJS.Timeout>>(new Map());

    // Function to open accordion panel and highlight it
    const openAndHighlightPanel = useCallback((panelIndex: number) => {
      // Open the panel if it's not already open
      setOpenPanels((prev) => {
        if (prev.includes(panelIndex)) {
          return prev;
        }
        return [...prev, panelIndex].sort((a, b) => a - b);
      });

      // Add amber border highlight
      setHighlightedPanels((prev) => new Set(prev).add(panelIndex));

      // Clear any existing timeout for this panel
      const existingTimeout = highlightTimeoutsRef.current.get(panelIndex);
      if (existingTimeout) {
        clearTimeout(existingTimeout);
      }

      // Remove highlight after 5 seconds
      const timeoutId = setTimeout(() => {
        setHighlightedPanels((prev) => {
          const next = new Set(prev);
          next.delete(panelIndex);
          return next;
        });
        // Remove timeout ID from ref after it runs
        highlightTimeoutsRef.current.delete(panelIndex);
      }, 5000);

      // Store timeout ID in ref
      highlightTimeoutsRef.current.set(panelIndex, timeoutId);
    }, []);

    // Cleanup timeouts on unmount
    useEffect(() => {
      return () => {
        // Clear all remaining timeouts
        highlightTimeoutsRef.current.forEach((timeoutId) => {
          clearTimeout(timeoutId);
        });
        // Empty the ref
        highlightTimeoutsRef.current.clear();
      };
    }, []);

    // Memoize submit button disabled state
    const isSubmitDisabled = useMemo(
      () => !formikProps.isValid || formikProps.isSubmitting,
      [formikProps.isValid, formikProps.isSubmitting]
    );

    return (
      <SideLayout willOverflow isPopup={isPopup}>
        <PromptEffect />
        {!isPopup && (
          <SideLayout.Head
            onClose={onClose}
            iconClass={isEditMode ? 'arrow-left' : 'x'}
          >
            {title}
          </SideLayout.Head>
        )}
        <SideLayout.Body>
          {assetId && <StatusToggle />}
          <AIAnalysisDropzone
            onFieldsUpdated={openAndHighlightPanel}
            assetTypes={assetTypes}
          />
          <Accordion
            mode='independent'
            openPanel={openPanels}
            onToggledPanel={(idx) => {
              if (idx !== undefined) {
                setOpenPanels((prev) => {
                  if (prev.includes(idx)) {
                    return prev.filter((i) => i !== idx);
                  }
                  return [...prev, idx].sort((a, b) => a - b);
                });
              }
            }}
          >
            {/* What Section - Original Form Fields */}
            <Accordion.GreyCard
              data-usetiful='what'
              error={validationChecks.hasWhatSectionErrors}
              className={
                highlightedPanels.has(0)
                  ? 'border-2 !border-amber-300'
                  : undefined
              }
            >
              <Accordion.Button>Summary</Accordion.Button>
              <Accordion.Panel>
                <Accordion.PanelSection>
                  <TextField name='name' label={t('name')} required />
                  <AssetTypeField onAssetTypesChange={setAssetTypes} />
                  {/* <AssetModelField /> */}
                  <TextAreaField name='notes' label={t('notes')} />

                  {/* Image Upload Section */}
                  <ImageUploadSection
                    hasAssetImage={validationChecks.hasAssetImage}
                    deploymentImage={deploymentImage}
                  />
                </Accordion.PanelSection>
              </Accordion.Panel>
            </Accordion.GreyCard>

            {/* Additional Info Section - Attributes (always show) */}
            <Accordion.GreyCard
              data-usetiful='details'
              className={
                highlightedPanels.has(1)
                  ? 'border-2 !border-amber-300'
                  : undefined
              }
            >
              <Accordion.Button>Details</Accordion.Button>
              <Accordion.Panel>
                <Accordion.PanelSection>
                  <AttributeFormikField entityType='Asset' attachments={{}} />
                </Accordion.PanelSection>
              </Accordion.Panel>
            </Accordion.GreyCard>

            {/* Where Section - Deployment Fields (only show for genuine new asset creation) */}
            {!assetId && !isPopup && (
              <Accordion.GreyCard
                data-usetiful='deploy'
                error={validationChecks.hasDeploySectionErrors}
              >
                <Accordion.Button>Deploy</Accordion.Button>
                <Accordion.Panel>
                  <Accordion.PanelSection>
                    <DeploymentFields />
                  </Accordion.PanelSection>
                </Accordion.Panel>
              </Accordion.GreyCard>
            )}
          </Accordion>
        </SideLayout.Body>
        <SideLayout.Foot className='p-4'>
          <SubmitButton data-usetiful='submit' disabled={isSubmitDisabled}>
            {isEditMode ? t('saveChanges') : `${t('create')} ${t('asset')}`}
          </SubmitButton>
        </SideLayout.Foot>
      </SideLayout>
    );
  }
);
AssetFormContent.displayName = 'AssetFormContent';

// Memoized image upload section to prevent unnecessary re-renders
const ImageUploadSection = React.memo(
  ({
    hasAssetImage,
    deploymentImage,
  }: {
    hasAssetImage: boolean;
    deploymentImage?: string;
  }) => {
    const { t } = useTranslation();

    return (
      <div className='mb-3 mt-4'>
        {!hasAssetImage && deploymentImage && (
          <>
            <Label>{t('Image')}</Label>
            <div className='mb-3 flex flex-col items-center gap-2 p-2'>
              <Thumb kind='image' url={deploymentImage} />
              <p className='mb-1 text-xs italic text-gray-500'>
                {t('currentImageFromDeployment')}
              </p>
            </div>
          </>
        )}
        <SingleUploadField
          hasLabel={hasAssetImage || !deploymentImage}
          id='assetImage'
          name='images'
          imagesOnly
          isMainUpload
        />
      </div>
    );
  }
);
ImageUploadSection.displayName = 'ImageUploadSection';

// Helper function to compress image for AI analysis
// Uses Canvas API to resize and compress images before sending to API
const compressImageForAnalysis = async (
  blob: Blob,
  maxWidth: number = 1024,
  maxHeight: number = 1024,
  quality: number = 0.75
): Promise<Blob> => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const url = URL.createObjectURL(blob);

    img.onload = () => {
      URL.revokeObjectURL(url);

      // Calculate new dimensions while maintaining aspect ratio
      let width = img.width;
      let height = img.height;

      if (width > maxWidth || height > maxHeight) {
        const ratio = Math.min(maxWidth / width, maxHeight / height);
        width = width * ratio;
        height = height * ratio;
      }

      // Create canvas and draw resized image
      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext('2d');

      if (!ctx) {
        reject(new Error('Failed to get canvas context'));
        return;
      }

      ctx.drawImage(img, 0, 0, width, height);

      // Convert to blob with compression
      canvas.toBlob(
        (compressedBlob) => {
          if (compressedBlob) {
            resolve(compressedBlob);
          } else {
            reject(new Error('Failed to compress image'));
          }
        },
        blob.type || 'image/jpeg',
        quality
      );
    };

    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('Failed to load image for compression'));
    };

    img.src = url;
  });
};

// AI Analysis Dropzone component - slim dropzone for auto-filling details via AI
const AIAnalysisDropzone = React.memo(
  ({
    onFieldsUpdated,
    assetTypes,
  }: {
    onFieldsUpdated?: (panelIndex: number) => void;
    assetTypes: string[];
  }) => {
    const formik = useFormikContext<AssetFormValues>();
    const { setFieldValue, setValues, values } = formik;
    const [isAnalyzing, setIsAnalyzing] = useState(false);

    // Query available attributes for Asset entity type
    const [attributesResult] = useQuery({
      query: AssetAttributesForAI_Query,
      variables: { entityType: 'Asset' },
      requestPolicy: 'cache-first', // Ensure we always have fresh data
    });

    const handleImageAnalysis = useCallback(
      async (uploadResults: UploadResult[]) => {
        if (uploadResults.length === 0) return;

        // Capture current form values at the start of async operation
        // to avoid stale closure issues if user modifies form during analysis
        const currentType = values.type;
        const currentName = values.name;
        const currentAttributes = values.attributes || [];

        const imageId = uploadResults[0].id;
        setIsAnalyzing(true);

        try {
          // Get the file blob from localforage storage
          // Add retry mechanism to handle race condition where storage write hasn't completed yet
          const { loadPreviewsFromStorage } = await import(
            '~/components/upload2/Upload'
          );

          let storedImage: { id: string; data: Blob } | undefined;
          const maxRetries = 5;
          const retryDelay = 100; // 100ms between retries

          for (let attempt = 0; attempt < maxRetries; attempt++) {
            const storedPreviews = await loadPreviewsFromStorage();
            storedImage = storedPreviews.find((img) => img.id === imageId);

            if (storedImage) {
              break; // Found the image, exit retry loop
            }

            // Wait before retrying (except on last attempt)
            if (attempt < maxRetries - 1) {
              await new Promise((resolve) => setTimeout(resolve, retryDelay));
            }
          }

          if (!storedImage) {
            throw new Error(
              `Uploaded image not found in storage after ${maxRetries} attempts. The file may not have been saved properly.`
            );
          }

          // Compress image for AI analysis (smaller file = faster upload, lower API costs)
          // Max dimensions: 1024x1024, Quality: 0.75 (good balance for text/sticker recognition)
          const compressedBlob = await compressImageForAnalysis(
            storedImage.data,
            1024, // maxWidth
            1024, // maxHeight
            0.75 // quality
          );

          // Convert compressed blob to File for FormData
          const file = new File(
            [compressedBlob],
            uploadResults[0].name || 'image.jpg',
            { type: compressedBlob.type || 'image/jpeg' }
          );

          // Create FormData for multipart/form-data request
          const formData = new FormData();
          formData.append('image', file);

          if (assetTypes.length > 0) {
            formData.append('assetTypes', JSON.stringify(assetTypes));
          }

          // Use server-side endpoint - API key handled server-side, session cookie handles auth
          const analyzeApiUrl = '/api/analyze-asset-image';

          // Set up timeout using AbortController
          const timeoutMs = 30000; // 30 seconds timeout
          const abortController = new AbortController();
          let timeoutId: ReturnType<typeof setTimeout> | null = setTimeout(
            () => {
              abortController.abort();
            },
            timeoutMs
          );

          try {
            // Call server-side API endpoint - no API key needed (handled server-side)
            // Session cookie automatically handles authentication
            const response = await fetch(analyzeApiUrl, {
              method: 'POST',
              body: formData,
              signal: abortController.signal,
            });

            // Clear timeout on successful fetch start
            if (timeoutId) {
              clearTimeout(timeoutId);
              timeoutId = null;
            }

            // Check if response is ok
            if (!response.ok) {
              let errorMessage = `AI analysis failed: ${response.status} ${response.statusText}`;
              const contentType = response.headers.get('content-type');
              try {
                if (contentType && contentType.includes('application/json')) {
                  const errorData = await response.json();
                  errorMessage =
                    errorData.message || errorData.error || errorMessage;
                } else {
                  const errorText = await response.text();
                  if (errorText) {
                    errorMessage = `${errorMessage}. ${errorText}`;
                  }
                }
              } catch {
                // Fallback if reading response fails
              }
              throw new Error(errorMessage);
            }

            const contentType = response.headers.get('content-type');
            if (!contentType || !contentType.includes('application/json')) {
              throw new Error(
                'AI analysis endpoint returned non-JSON response. Endpoint may not be implemented.'
              );
            }

            const result = await response.json();

            // Server endpoint returns: { success: true, data: {...analysis results...} }
            if (!result.success || !result.data?.analysis?.objects?.[0]) {
              throw new Error(
                result.error ||
                  result.message ||
                  'AI analysis did not return valid appliance information'
              );
            }

            // Extract appliance information from analysis
            const appliance = result.data.analysis.objects[0];
            const availableAttributes = attributesResult.data?.attributes || [];
            const updates = applyAiAnalysisToForm({
              currentType,
              currentName,
              currentAttributes,
              liveAttributes: values.attributes,
              appliance,
              availableAttributes,
              uploadedImage: uploadResults[0] ?? null,
            });

            // Apply updates to form
            if (Object.keys(updates).length > 0) {
              // Build updated values object - ensure all required fields are present
              const updatedValues: AssetFormValues = { ...values };
              for (const [key, value] of Object.entries(updates)) {
                if (value !== undefined && value !== null) {
                  (updatedValues as Record<string, unknown>)[key] = value;
                }
              }

              // Use setValues to update all fields at once - this ensures Formik processes them together
              setValues(updatedValues, false);

              // Also set each field individually to ensure Combobox components pick up the changes
              // Some components may need individual setFieldValue calls to trigger their internal state updates
              for (const [key, value] of Object.entries(updates)) {
                if (value !== undefined && value !== null) {
                  setFieldValue(key as keyof AssetFormValues, value, false);

                  // For type field specifically, also mark as touched to ensure Combobox displays it
                  if (key === 'type') {
                    // Use setTimeout to ensure the value is set first
                    setTimeout(() => {
                      setFieldValue('type', value, true); // Set with validation to trigger touch
                    }, 10);
                  }
                  // For name field, mark as touched to ensure TextField displays it
                  if (key === 'name') {
                    // Use setTimeout to ensure the value is set first
                    setTimeout(() => {
                      setFieldValue('name', value, true); // Set with validation to trigger touch
                    }, 10);
                  }
                }
              }

              // Small delay to ensure Formik has processed all updates
              await new Promise((resolve) => setTimeout(resolve, 150));

              // Verify the type was actually set
              if (updates.type) {
                const currentTypeValue = values.type;
                if (currentTypeValue !== updates.type) {
                  // Try one more time with a direct setFieldValue call
                  setFieldValue('type', updates.type, false);
                }
              }
              // Verify the name was actually set
              if (updates.name) {
                const currentNameValue = values.name;
                if (currentNameValue !== updates.name) {
                  // Try one more time with a direct setFieldValue call
                  setFieldValue('name', updates.name, false);
                }
              }
            }

            // Open and highlight accordion sections for updated fields
            if ((updates.type || updates.name) && onFieldsUpdated) {
              // Summary section (index 0) contains the type and name fields
              onFieldsUpdated(0);
            }
            if (
              updates.attributes &&
              updates.attributes.length > 0 &&
              onFieldsUpdated
            ) {
              // Details section (index 1) contains the attributes
              onFieldsUpdated(1);
            }

            const updatedFields = [];
            if (updates.name) updatedFields.push('name');
            if (updates.type) updatedFields.push('type');
            if (updates.attributes && updates.attributes.length > 0) {
              updatedFields.push('attributes');
            }
            const fieldsText =
              updatedFields.length > 0
                ? `Asset ${updatedFields.join(', ')} updated.`
                : 'Analysis completed.';
            toast.success(`AI analysis completed. ${fieldsText}`);
          } catch (err) {
            // Clear timeout if it's still active
            if (timeoutId) {
              clearTimeout(timeoutId);
              timeoutId = null;
            }

            // Handle timeout/abort errors specifically
            if (err instanceof Error && err.name === 'AbortError') {
              toast.error(
                'AI analysis request timed out. Please try again with a smaller image or check your connection.'
              );
            } else {
              // Handle other fetch errors
              const errorMessage =
                err instanceof Error
                  ? err.message
                  : 'AI analysis request failed. Please try again.';
              toast.error(errorMessage);
            }
          } finally {
            setIsAnalyzing(false);
          }
        } catch (outerErr) {
          // Handle errors from storage loading, compression, etc.
          setIsAnalyzing(false);
          const errorMessage =
            outerErr instanceof Error
              ? outerErr.message
              : 'Failed to process image for analysis. Please try again.';
          toast.error(errorMessage);
        }
      },
      [
        setFieldValue,
        setValues,
        values,
        attributesResult.data,
        onFieldsUpdated,
        assetTypes,
      ]
    );

    return (
      <div className='mb-3 flex w-full flex-col items-center justify-center rounded-md border border-amber-300 bg-amber-100 px-4 py-3 shadow-sm'>
        <Upload
          className='w-full '
          id='assetAutofillImage'
          disabled={isAnalyzing}
          onUploaded={handleImageAnalysis}
          onUploadComplete={(closeDialog: () => void) => {
            // Close the upload dialog immediately when upload completes
            closeDialog();
          }}
        >
          <div
            className={`mx-auto flex items-center gap-3 rounded-lg border border-dashed border-outline-variant bg-white px-4 pb-3 pt-4 transition-colors ${
              isAnalyzing
                ? 'pointer-events-none cursor-not-allowed opacity-50'
                : 'cursor-pointer hover:border-primary hover:bg-surface-container-bright'
            }`}
            role='button'
            tabIndex={isAnalyzing ? -1 : 0}
            aria-label='Upload image for AI analysis to auto-fill details'
            aria-disabled={isAnalyzing}
            onClick={isAnalyzing ? (e) => e.preventDefault() : undefined}
            onKeyDown={
              isAnalyzing
                ? (e) => {
                    e.preventDefault();
                  }
                : undefined
            }
          >
            <FontAwesomeIcon
              icon={faMagicWandSparkles}
              className={`h-4 w-4 flex-shrink-0 ${
                isAnalyzing ? 'animate-spin text-primary/50' : 'text-primary'
              }`}
            />
            <span className='text-sm text-primary'>
              {isAnalyzing
                ? 'Analyzing image...'
                : 'Upload image to auto-fill details'}
            </span>
          </div>
        </Upload>
        <p className='mr-auto mt-2 pl-1 text-left text-xs text-primary'>
          Take a clear photo of the appliance&#39;s information sticker (brand,
          model, serial). Make sure all text is visible and easy to read.
        </p>
      </div>
    );
  }
);
AIAnalysisDropzone.displayName = 'AIAnalysisDropzone';

export function AssetForm({ id, isPopup, onSuccess, asset: assetProp }: Props) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams();
  const { assetId } = params;
  const [searchParams] = useSearchParams();

  // Use context to get assets and reload function
  const { assets } = useAssetContext();

  // ✨ STEP 1: Initialize deployment storage hook
  const deploymentStorage = useDeploymentStorage();

  // ✨ STEP 2: Detect site context from URL parameters or route
  // Check if asset is being created from a site view using route params or query params
  const siteContext = useMemo(() => {
    // First try to get from route parameters (preferred with relative routes)
    const routeSiteId = params.siteId;

    if (routeSiteId) {
      return { siteId: routeSiteId, siteName: null }; // We'll get the name from the query
    }

    // Fallback to query parameters (legacy support)
    const siteId = searchParams.get('siteId');
    const siteName = searchParams.get('siteName');

    if (siteId && siteName) {
      return { siteId, siteName };
    }

    return null;
  }, [params.siteId, searchParams]);

  // Fetch complete site data when we have siteId from URL context
  const [siteDataResult] = useQuery({
    query: GetSiteForContext,
    variables: { id: siteContext?.siteId || '' },
    pause: !siteContext?.siteId,
  });

  // const siteFromQuery = siteDataResult.data?.site;

  // Handle site data query errors
  useEffect(() => {
    if (siteDataResult.error && siteContext?.siteId) {
      toast.error(
        'Failed to load site information. Some features may not work properly.'
      );
    }
  }, [siteDataResult.error, siteContext?.siteId]);

  // Determine effective asset ID
  const effectiveAssetId = isPopup ? id : assetId;

  // Fallback query for when asset is not in context (e.g., site context)
  const [{ data: fallbackAssetData }] = useQuery({
    query: AssetDocument,
    variables: { id: effectiveAssetId || '' },
    pause: !effectiveAssetId || assets.some((a) => a.id === effectiveAssetId),
    requestPolicy: 'cache-first',
  });

  // Check for cloning state from navigation
  const cloneFromAsset = useMemo(() => {
    const state = location.state as { cloneFrom?: AssetType } | null;
    return state?.cloneFrom || null;
  }, [location.state]);

  // Find the asset from context, passed prop, or fallback query
  const asset = useMemo(() => {
    // If asset is passed as prop, use it (this fixes site context issues)
    if (assetProp) return assetProp;

    // Try to find from context first
    if (effectiveAssetId) {
      const contextAsset = assets.find((a) => a.id === effectiveAssetId);
      if (contextAsset) return contextAsset;
    }

    // Fallback to direct query result
    return fallbackAssetData?.asset || null;
  }, [assetProp, assets, effectiveAssetId, fallbackAssetData]);

  const [, create] = useCreateAssetMutation();
  const [, update] = useUpdateAssetMutation();
  const [, updateAttributes] = useMutation(UpdateAttributeValues);
  const [, createDeployment] = useCreateDeploymentMutation();

  // Memoize deployment data to prevent unnecessary re-renders
  const deploymentData = useMemo(() => asset?.deployment, [asset?.deployment]);

  // Get deployment image URL from the memoized data
  const deploymentImage = deploymentData?.image;

  // Detect if we're in edit mode
  const isEditMode = useMemo(() => !!effectiveAssetId, [effectiveAssetId]);

  const title = assetId ? t('editAsset') : t('createAsset');

  // Use ref to stabilize default date like DeploymentForm
  const defaultDateRef = useRef<Date>(new Date());

  // Memoize the close handler to prevent recreation on every render
  const handleClose = useCallback(() => {
    if (isEditMode) {
      // When editing an asset, navigate back to the asset view with stored tab state
      const storedTab = effectiveAssetId
        ? sessionStorage.getItem(`assetTab_${effectiveAssetId}`)
        : null;
      const tabParam = storedTab ? `?tab=${storedTab}` : '';

      if (siteContext) {
        // In site context, navigate back to site asset view
        navigate(
          `/sites/${siteContext.siteId}/assets/${effectiveAssetId}${tabParam}`
        );
      } else {
        // In regular asset context, navigate back to asset view
        navigate(`/assets/${effectiveAssetId}${tabParam}`);
      }
    } else if (siteContext) {
      // When in site context, navigate back to the site view (not the assets route which has no element)
      // This goes from /sites/123/assets/new -> /sites/123
      navigate('../../');
    } else {
      // Default behavior - navigate to assets list
      // This goes from /assets/new -> /assets
      navigate('..');
    }
  }, [navigate, siteContext, isEditMode, effectiveAssetId]);

  // Move session storage read to useEffect to avoid side effects during render
  const [savedDeploymentInfo, setSavedDeploymentInfo] =
    useState<ReturnType<typeof deploymentStorage.get>>(null);

  useEffect(() => {
    const savedInfo = deploymentStorage.get();
    setSavedDeploymentInfo(savedInfo);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Run only once on mount

  // Handle side effects for clearing stale deployment storage
  // This must be in useEffect, not useMemo, to avoid React rule violations
  useEffect(() => {
    if (
      siteContext &&
      savedDeploymentInfo &&
      savedDeploymentInfo.siteId !== siteContext.siteId
    ) {
      // Clear any stale deployment storage when we have site context from URL
      // This prevents conflicts between URL context and saved deployment info
      deploymentStorage.clear();
      setSavedDeploymentInfo(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [siteContext, savedDeploymentInfo]);

  // Memoize initial values to prevent form recreation - PURE COMPUTATION ONLY
  const initialValues: AssetFormValues = useMemo(() => {
    // Priority 0: Cloning from another asset
    if (cloneFromAsset && !asset) {
      return {
        id: null,
        status: Status.Active,
        name: `${cloneFromAsset.name} [Copy]`,
        type: cloneFromAsset.type,
        notes: cloneFromAsset.notes || '',
        modelId: cloneFromAsset.model?.id
          ? Number(cloneFromAsset.model.id)
          : null,
        attributes: cloneFromAsset.attributes
          ? (
              cloneFromAsset.attributes as Array<{
                id: string;
                value?: unknown;
              }>
            ).map((attr) => ({
              id: attr.id,
              value: (() => {
                if (Array.isArray(attr.value)) {
                  return (attr.value[0] as string) || '';
                }
                if (typeof attr.value === 'string') {
                  return attr.value;
                }
                if (attr.value == null) {
                  return '';
                }
                return String(attr.value);
              })(),
            }))
          : [],
        images: convertAssetImagesToUploadResults(cloneFromAsset),
        sites: [],
        spaceName: '',
        deploymentImages: [],
        startDate: cloneFromAsset.deployment?.startedAt
          ? new Date(cloneFromAsset.deployment.startedAt)
          : defaultDateRef.current,
      };
    }

    // Priority 1: For existing assets, use their data
    if (asset) {
      return {
        id: asset.id,
        status: asset.status as Status,
        name: asset.name,
        type: asset.type,
        notes: asset.notes || '',
        modelId: asset.model?.id ? Number(asset.model.id) : null,
        attributes: asset.attributes
          ? (asset.attributes as Array<{ id: string; value?: unknown }>).map(
              (attr) => ({
                id: attr.id,
                value: (() => {
                  if (Array.isArray(attr.value)) {
                    return (attr.value[0] as string) || '';
                  }
                  if (typeof attr.value === 'string') {
                    return attr.value;
                  }
                  if (attr.value == null) {
                    return '';
                  }
                  return String(attr.value);
                })(),
              })
            )
          : [],
        images: convertAssetImagesToUploadResults(asset),
        // For existing assets with deployment, populate deployment fields
        sites: deploymentData ? [] : [], // TODO: Add deployment site logic if needed
        spaceName: deploymentData?.space?.name || '',
        deploymentImages: [],
        startDate: deploymentData?.startedAt
          ? new Date(deploymentData.startedAt)
          : defaultDateRef.current,
      };
    }
    // Priority 2: Site context
    if (siteContext) {
      // Enhanced logic: If coming from site and siteId matches storage siteId, also fill space field
      const shouldPrePopulateSpace =
        savedDeploymentInfo &&
        savedDeploymentInfo.siteId === siteContext.siteId &&
        savedDeploymentInfo.spaceName;

      return {
        id: null,
        status: Status.Active,
        name: '',
        type: '',
        notes: '',
        modelId: null,
        attributes: [],
        images: [],
        // Pre-populate deployment section with site ID from URL
        // SiteSelect will find the full site data (including image) from the sites prop
        sites: [siteContext.siteId],
        spaceName: shouldPrePopulateSpace ? savedDeploymentInfo.spaceName : '', // Enhanced: pre-populate space if siteId matches
        deploymentImages: [],
        startDate: defaultDateRef.current,
      };
    }

    // Priority 3: Saved deployment info from session storage
    if (savedDeploymentInfo) {
      return {
        id: null,
        status: Status.Active,
        name: '',
        type: '',
        notes: '',
        modelId: null,
        attributes: [],
        images: [],
        // Pre-populate deployment section with saved site ID
        // SiteSelect will find the full site data (including image) from the sites prop
        sites: [savedDeploymentInfo.siteId],
        spaceName: savedDeploymentInfo.spaceName,
        deploymentImages: [],
        startDate: defaultDateRef.current,
      };
    }

    // Priority 4: Default empty form (no saved info)
    return {
      id: null,
      status: Status.Active,
      name: '',
      type: '',
      notes: '',
      modelId: null,
      attributes: [],
      images: [],
      sites: [],
      spaceName: '',
      deploymentImages: [],
      startDate: defaultDateRef.current,
    };
  }, [cloneFromAsset, asset, siteContext, savedDeploymentInfo, deploymentData]);

  // Helper function to handle asset mutation errors consistently
  const handleAssetMutationError = useCallback(
    (
      error: {
        message: string;
        graphQLErrors?: Array<{
          extensions?: { sentryEventId?: string };
        }>;
      },
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      _operation: 'create' | 'update',
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      _context: {
        assetId?: string;
        input: AssetInput;
      }
    ) => {
      // Extract Sentry ID from GraphQL errors
      let sentryEventId: string | undefined;
      if (error.graphQLErrors && error.graphQLErrors.length > 0) {
        const eventId = error.graphQLErrors[0]?.extensions?.sentryEventId;
        sentryEventId = typeof eventId === 'string' ? eventId : undefined;
      }

      // Preserve the original error to maintain graphQLErrors for outer catch
      const enhancedError = error as Error & {
        graphQLErrors?: typeof error.graphQLErrors;
        sentryEventId?: string;
      };
      enhancedError.sentryEventId = sentryEventId;
      return enhancedError;
    },
    []
  );

  const handleSubmit = useCallback(
    async (values: AssetFormValues) => {
      // Collect validation errors for shake animation
      const errors = [];

      if (!values.name || values.name.trim() === '') {
        errors.push('name');
      }

      if (!values.type || values.type.trim() === '') {
        errors.push('type');
      }

      // Check space name validation when a place is selected
      if (
        values.sites &&
        values.sites.length > 0 &&
        (!values.spaceName || values.spaceName.trim() === '')
      ) {
        errors.push('spaceName');
      }

      if (errors.length > 0) {
        // Form has validation errors - components will automatically animate
        // due to Formik setting error state on fields
        return;
      }

      const {
        id,
        status,
        // asset_model_id, // Commented out - not used currently
        attachments,
        images,
        attributes,
        sites,
        location,
        spaceName,
        startDate,
        deploymentImages,
        name,
        type,
        notes,
      } = values;

      // Convert asset_model_id to number if present
      // const modelId = asset_model_id?.length
      //   ? parseInt(asset_model_id[0], 10)
      //   : null;
      // Convert images UploadResult[] back to string[] by mapping over their IDs
      const imageIds = images.map((img) => img.id);

      // Only include valid AssetInput fields
      const input: AssetInput = {
        name,
        type,
        notes,
        status: status, // Already Status type
        // modelId: modelId, // Commented out - not used currently
        attachments: attachments ?? [], // Ensure attachments is not undefined
        images: imageIds,
      };

      try {
        let assetResult;
        let createdAssetId: string | null = null;

        // Phase 1: Create/Update Asset
        if (id) {
          // Update existing asset
          const { data, error } = await update({ id, input });
          if (error) {
            throw handleAssetMutationError(error, 'update', {
              assetId: id,
              input,
            });
          }
          assetResult = data?.updateAsset;
          createdAssetId = id;
        } else {
          // Create new asset
          const { data, error } = await create({ input });
          if (error) {
            throw handleAssetMutationError(error, 'create', {
              input,
            });
          }
          assetResult = data?.createAsset;
          createdAssetId = assetResult?.id || null;
        }

        if (!assetResult || !createdAssetId) {
          throw new Error('Failed to create/update asset');
        }

        // Phase 2: Update attributes if any are provided
        if (attributes.length > 0) {
          try {
            await updateAttributes({
              input: {
                id: `Asset:${createdAssetId}`,
                attributes: attributes.filter(
                  (attr) => attr.value.trim() !== ''
                ),
              },
            });
          } catch {
            toast.warning(
              'Asset created successfully, but failed to save attributes. You can add them later from the asset view.'
            );
            // Continue - attributes are optional, don't fail the whole process
          }
        }

        // Use same logic as DeploymentForm: prioritize sites over deploymentLocation
        let hasDeploymentInfo = false;
        let locationId: string | undefined;

        if (sites && sites.length > 0) {
          // Fix: sites array contains string IDs, not Site objects
          const siteId = typeof sites[0] === 'string' ? sites[0] : sites[0].id;
          locationId = `Site:${siteId}`;
          hasDeploymentInfo = true;
        } else {
          if (location?.id?.length) {
            locationId = `${location.type}:${location.id.join('')}`;
            hasDeploymentInfo = true;
          }
        }

        if (hasDeploymentInfo && locationId && spaceName && startDate) {
          try {
            const deploymentImageIds =
              deploymentImages?.map((img) => img.id) || [];

            const deploymentInput = {
              assetId: createdAssetId,
              locationId,
              spaceName,
              startDate,
              images: deploymentImageIds,
            };

            const result = await createDeployment({
              input: deploymentInput,
            });

            // ✅ DEPLOYMENT STORAGE TEST: Save deployment info after successful creation
            if (result?.data?.createDeployment) {
              // Determine site info for storage
              let siteId: string = '';
              let siteName: string = '';

              if (sites && sites.length > 0) {
                // Fix: sites array contains string IDs, not Site objects
                if (typeof sites[0] === 'string') {
                  siteId = sites[0];
                  siteName = 'Site'; // Fallback name since we only have ID
                } else {
                  siteId = sites[0].id;
                  siteName = sites[0].name;
                }
              } else if (location?.type === 'Site' && location.id.length > 0) {
                siteId = location.id[0];
                siteName = 'Site'; // Fallback name
              }

              if (siteId && spaceName) {
                deploymentStorage.save({
                  siteId,
                  siteName,
                  spaceName,
                });
              }
            }
          } catch {
            toast.warning(
              'Asset created successfully, but failed to create deployment. You can deploy it later from the asset view.'
            );
            // Continue - deployment is optional, don't fail the whole process
          }
        } else {
          // Show warning if user provided partial deployment info but it's incomplete

          if (hasDeploymentInfo && locationId) {
            // User selected a site/location but missing other required fields
            const missingFields = [];
            if (!spaceName) missingFields.push('space name');
            if (!startDate) missingFields.push('start date');

            if (missingFields.length > 0) {
              toast.warning(
                `Asset created successfully, but deployment was skipped due to missing: ${missingFields.join(
                  ', '
                )}. You can deploy it later from the asset view.`
              );
            }
          }
        }

        // Success - navigate to the created/updated asset
        if (createdAssetId) {
          onSuccess?.(createdAssetId);
          if (!onSuccess) {
            if (siteContext) {
              // When in site context, navigate back to site view
              navigate('../../');
            } else {
              // Default behavior - navigate to asset view
              navigate(`/assets/${createdAssetId}`);
            }
          }
        }
        // Show warnings when optional steps fail
      } catch {
        // Display a general error message with contact us option
        toast.error(<div>Failed to {id ? 'update' : 'create'} asset.</div>, {
          autoClose: 8000,
        });
      }
    },
    [
      update,
      create,
      updateAttributes,
      createDeployment,
      onSuccess,
      navigate,
      deploymentStorage,
      siteContext,
      handleAssetMutationError,
    ]
  );

  // Since we're using assets from context, no need for separate loading state
  // The parent Assets component handles loading

  return (
    <Formik
      initialValues={initialValues}
      onSubmit={handleSubmit}
      validationSchema={validationSchema}
      enableReinitialize
      validateOnMount
    >
      {(formikProps) => (
        <AssetFormContent
          formikProps={formikProps}
          assetId={assetId}
          isPopup={isPopup}
          title={title}
          deploymentImage={deploymentImage || undefined}
          onClose={handleClose}
          isEditMode={isEditMode}
        />
      )}
    </Formik>
  );
}

const AssetTypeField = React.memo(
  ({
    onAssetTypesChange,
  }: {
    onAssetTypesChange?: (assetTypes: string[]) => void;
  }) => {
    const { ability } = useAppContext();
    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const [newlyCreatedTypes, setNewlyCreatedTypes] = useState<string[]>([]);
    const [result, reexecuteQuery] = useAssetFiltersQuery({
      requestPolicy: 'cache-first', // Use cache first instead of network-only
    });
    const { setFieldValue } = useFormikContext<AssetFormValues>();
    const [configResult, refetchConfig] = useQuery({
      query: AssetTypeConfigForFormDocument,
      requestPolicy: 'cache-and-network',
    });

    const canManageAccount = Boolean(ability?.can?.('manage', 'Account'));

    // Memoize ordered asset types using server config + in-use extras + newly created types
    // Uses pure helper functions defined above for data transformation
    const assetTypes = useMemo((): string[] => {
      const configuredClean = extractConfiguredTypes(configResult.data);
      const inUseClean = extractInUseTypes(result.data);

      // Filter newly created types that aren't duplicates
      // This logic stays inline since it depends on component state (newlyCreatedTypes)
      const allNewTypes = newlyCreatedTypes.filter(
        (t) =>
          !configuredClean.includes(t) &&
          !inUseClean.includes(t) &&
          t.trim() !== '' &&
          t.toLowerCase() !== 'other'
      );

      const extras = getExtras(configuredClean, inUseClean);
      const mergedTypes = mergeTypes(
        configuredClean,
        extras,
        allNewTypes,
        inUseClean
      );
      const orderMode = configResult.data?.assetTypeConfig?.orderMode;

      return applyOrdering(mergedTypes, orderMode, configuredClean);
    }, [configResult.data, result.data, newlyCreatedTypes]);

    // Notify parent component when asset types change
    useEffect(() => {
      if (onAssetTypesChange) {
        onAssetTypesChange(assetTypes);
      }
    }, [assetTypes, onAssetTypesChange]);

    // Handle query errors
    useEffect(() => {
      if (result.error) {
        toast.error(
          'Failed to load asset types. You can still create assets by typing the type manually.'
        );
      }
    }, [result.error]);

    // Clean up newly created types once they appear in server data
    useEffect(() => {
      if (newlyCreatedTypes.length === 0) return;

      const configured: string[] = Array.isArray(
        configResult.data?.assetTypeConfig?.types
      )
        ? (configResult.data?.assetTypeConfig?.types as string[])
        : [];
      const inUseRaw: unknown = result.data?.assetTypes;
      const inUse: string[] = Array.isArray(inUseRaw)
        ? (inUseRaw as string[])
        : [];

      const serverTypes = [...configured, ...inUse];
      const stillNew = newlyCreatedTypes.filter(
        (t) => !serverTypes.includes(t)
      );

      if (stillNew.length !== newlyCreatedTypes.length) {
        setNewlyCreatedTypes(stillNew);
      }
    }, [
      configResult.data?.assetTypeConfig?.types,
      result.data?.assetTypes,
      newlyCreatedTypes,
    ]);

    const handleAssetTypeChange = (value: string) => {
      if (value === '$NEW') {
        if (canManageAccount) {
          setIsDialogOpen(true);
        } else {
          toast.info('You do not have permission to create new asset types.');
        }
        return;
      }

      // If user lacks permission, block values not in the configured list
      if (!canManageAccount) {
        const inList = assetTypes.includes(value);
        if (!inList) {
          toast.info('Please select an existing asset type.');
          return;
        }
      }

      setFieldValue('type', value);
    };

    const handleAssetTypeCreated = (assetType: string) => {
      const trimmedType = assetType.trim();
      // Add to newly created types list so it appears in dropdown
      setNewlyCreatedTypes((prev) => {
        if (!prev.includes(trimmedType)) {
          return [...prev, trimmedType];
        }
        return prev;
      });
      // Set the newly created asset type as selected
      setFieldValue('type', trimmedType);
      // Refetch both queries to include the new one (in case it's been saved elsewhere)
      reexecuteQuery({ requestPolicy: 'network-only' });
      refetchConfig({ requestPolicy: 'network-only' });
    };

    return (
      <>
        <ComboboxField
          searchable
          name='type'
          label='Asset Type'
          dataUsetiful='asset-type'
          items={assetTypes}
          sortItems={false}
          required
          showCreateOption={canManageAccount}
          placeholder={
            result.error
              ? 'Type asset type manually'
              : 'Select or type asset type'
          }
          createAlwaysShown={canManageAccount}
          createText='Create Asset Type'
          onChange={handleAssetTypeChange}
        />
        {canManageAccount && (
          <CreateAssetTypeDialog
            isOpen={isDialogOpen}
            onClose={() => setIsDialogOpen(false)}
            onAssetTypeCreated={handleAssetTypeCreated}
          />
        )}
      </>
    );
  }
);
AssetTypeField.displayName = 'AssetTypeField';

// const AssetModelField = () => {
//   const { t } = useTranslation();
//   const [result] = useListAssetModelsQuery();
//   const { values, setFieldValue } = useFormikContext<{
//     type: string;
//     // asset_model_id?: string[]; // Commented out - not used currently
//   }>();
//   const models = result.data?.assetModels;

//   const filteredModelOptions = useMemo(() => {
//     const type = values.type;

//     return !models
//       ? []
//       : type
//       ? // @ts-ignore
//         models
//           .filter((opt) => type === opt.type)
//           .map((value) => ({
//             value: value.id,
//             label: value.displayName,
//           }))
//       : models.map((value) => ({
//           value: value.id,
//           label: value.displayName,
//         }));
//   }, [models, values.type]);

//   // reset the model field if asset type changes
//   useEffect(() => {
//     // setFieldValue('asset_model_id', []); // Commented out - not used currently
//   }, [values.type, setFieldValue]);

//   return (
//     <SelectField
//       name='asset_model_id'
//       label={t('model')}
//       options={filteredModelOptions}
//       disabled={!values.type}
//     />
//   );
// };

// Custom SpaceNameComboboxField that can handle both string IDs and Site objects
const AssetFormSpaceNameField = React.memo(
  ({
    placeholder,
    required = true,
  }: {
    placeholder?: string;
    required?: boolean;
  }) => {
    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const {
      values: { location, sites, spaceName },
      setFieldValue,
    } = useFormikContext<AssetFormValues>();

    // Use ref to track previous locationId to detect changes
    const prevLocationIdRef = useRef<string | undefined>(undefined);

    // Handle both string IDs and Site objects in the sites array
    let locationId: string | undefined;
    if (sites && sites.length > 0) {
      const firstSite = sites[0];
      // Check if it's a string ID or Site object
      if (typeof firstSite === 'string') {
        locationId = `Site:${firstSite}`;
      } else {
        locationId = `Site:${firstSite.id}`;
      }
    } else if (location?.id?.length && location.id.length > 0) {
      locationId = `${location.type}:${location.id[0]}`;
    }

    // Clear space field when place/location changes
    useEffect(() => {
      const prevLocationId = prevLocationIdRef.current;

      // Only clear if locationId actually changed and we had a previous location
      // This prevents clearing on initial mount
      if (
        prevLocationId !== undefined &&
        prevLocationId !== locationId &&
        spaceName
      ) {
        setFieldValue('spaceName', '');
      }

      // Update ref for next comparison
      prevLocationIdRef.current = locationId;
    }, [locationId, spaceName, setFieldValue]);

    const [result, reexecuteQuery] = useQuery<{
      location: { spaces: { id: string; name: string }[] };
    }>({
      query: gql`
        query ($locationId: ID!) {
          location(id: $locationId) {
            id
            spaces {
              id
              name
            }
          }
        }
      `,
      variables: { locationId },
      pause: !locationId,
    });

    // Sort spaces alphabetically
    const spaces = useMemo(() => {
      const spaceNames =
        result.data?.location.spaces?.map(({ name }) => name) || [];
      return [...spaceNames].sort((a, b) =>
        a.localeCompare(b, undefined, { sensitivity: 'base' })
      );
    }, [result.data?.location.spaces]);

    // Handle query errors
    useEffect(() => {
      if (result.error && locationId) {
        toast.error(
          'Failed to load available spaces. You can still type the space name manually.'
        );
      }
    }, [result.error, locationId]);

    const handleSpaceChange = (value: string) => {
      if (value === '$NEW') {
        // Open dialog for space creation
        setIsDialogOpen(true);
      } else {
        // Set the selected space name
        setFieldValue('spaceName', value);
      }
    };

    const handleSpaceCreated = (spaceName: string) => {
      // Set the newly created space as selected
      setFieldValue('spaceName', spaceName);
      // Refetch the spaces to include the new one
      reexecuteQuery({ requestPolicy: 'network-only' });
    };

    // Determine the effective placeholder
    const effectivePlaceholder = result.error
      ? 'Type space name manually'
      : placeholder;

    return (
      <>
        <ComboboxField
          name='spaceName'
          label='Space'
          items={spaces}
          disabled={!locationId}
          placeholder={effectivePlaceholder}
          searchable={true}
          createAlwaysShown={true}
          createText='Create Space'
          required={required}
          onChange={handleSpaceChange}
        />
        {locationId && (
          <CreateSpaceDialog
            isOpen={isDialogOpen}
            onClose={() => setIsDialogOpen(false)}
            locationId={locationId}
            onSpaceCreated={handleSpaceCreated}
          />
        )}
      </>
    );
  }
);
AssetFormSpaceNameField.displayName = 'AssetFormSpaceNameField';

// Deployment fields component defined after main component
const DeploymentFields = React.memo(() => {
  const { values } = useFormikContext<AssetFormValues>();

  // Dynamic placeholder logic like in DeploymentForm
  const hasLocationSelected =
    (values.location?.id?.length ?? 0) > 0 || (values.sites?.length ?? 0) > 0;
  const spaceNamePlaceholder = hasLocationSelected
    ? 'Select Space Name'
    : 'Select a place first';

  return (
    <>
      <SiteSelectField required={false} />
      <AssetFormSpaceNameField
        placeholder={spaceNamePlaceholder}
        required={hasLocationSelected}
      />
      <DateTimeField name='startDate' label='Start Date' required />
      <SingleUploadField
        name='deploymentImages'
        id='deployment-images'
        label='Deployment Images'
        imagesOnly
        multiple
      />
    </>
  );
});
DeploymentFields.displayName = 'DeploymentFields';
